<script setup lang="ts">
  import { ref, onMounted, computed, nextTick, watch } from 'vue'
  import { Eye, Pencil, Check, X, Minus } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import { Input } from '@/components/ui/input'
  import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow
  } from '@/components/ui/table'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import type { Document, CustomerCase } from '@/types/generated/types.gen'
  import { genericErrorToast } from '@/lib/toasts'

  const props = defineProps<{
    caseData?: CustomerCase
    documents?: Document[]
    integrationMode?: string
    debounceDelay?: number
    throttleInterval?: number
  }>()

  const emit = defineEmits(['updateDocuments', 'update:fileData', 'sizeValidation'])

  interface ExportDocument extends Document {
    isSelected: boolean
    isEditing: boolean
    newName: string
    size?: number
    sizeFormatted?: string
    exceedsJurnodesLimit?: boolean
    sizeEstimated?: boolean
  }

  const exportDocuments = ref<ExportDocument[]>([])
  const updateTrigger = ref(0)

  const isRaMicroApiMode = computed(() => props.integrationMode === 'RaMicroApi')
  const maxRaMicroApiFileSize = computed(() => {
    const limitMb = props.caseData?.org?.ra_micro_upload_limit_mb || 25
    return limitMb * 1024 * 1024
  })

  const maxRaMicroApiFileSizeMb = computed(() => {
    return props.caseData?.org?.ra_micro_upload_limit_mb || 25
  })

  /**
   * Fetches the file size from a URL using a HEAD request
   * @param url The URL to fetch the file size from
   * @returns Promise that resolves to the file size in bytes, or null if it couldn't be determined
   */
  const fetchFileSizeFromUrl = async (url: string): Promise<number | null> => {
    try {
      const response = await fetch(url, { method: 'HEAD' })
      if (!response.ok) {
        console.error(`Failed to fetch file size: ${response.status} ${response.statusText}`)
        return null
      }

      const contentLength = response.headers.get('content-length')
      if (contentLength) {
        return parseInt(contentLength, 10)
      }

      return null
    } catch (error) {
      console.error('Error fetching file size:', error)
      return null
    }
  }

  onMounted(async () => {
    try {
      // Use documents from props, or fall back to caseData.documents for backward compatibility
      const sourceDocuments = props.documents || props.caseData?.documents || []

      // Create initial document objects
      exportDocuments.value = await Promise.all(
        sourceDocuments.map(async (doc) => {
          // Try to determine file size or complexity using multiple properties
          let size = doc.filesize || 0
          let sizeEstimated = false
          if (size === 0 && doc.url) {
            // Try to get file size from URL
            const urlSize = await fetchFileSizeFromUrl(doc.url)
            if (urlSize) {
              size = urlSize
              sizeEstimated = true
            }
            // Fallback to estimation methods if URL fetch fails
            else if (doc.page_count && doc.page_count > 0) {
              // Rough estimate: ~100KB per page
              size = doc.page_count * 100 * 1024
              sizeEstimated = true
            } else if (doc.images && doc.images.length > 0) {
              // Rough estimate: ~100KB per image
              size = doc.images.length * 100 * 1024
              sizeEstimated = true
            }
          }

          // No need to check for Jurnodes limit anymore
          const exceedsLimit = false

          try {
            return {
              ...doc,
              isSelected: !exceedsLimit,
              isEditing: false,
              newName: doc.filename.replace('.pdf', ''),
              size: size,
              sizeFormatted: formatFileSize(size),
              sizeEstimated: sizeEstimated,
              exceedsJurnodesLimit: false
            }
          } catch (error) {
            genericErrorToast()
          }
        })
      )

      emitFileData()
    } catch (error) {
      genericErrorToast()
      console.error(error)
    }
  })

  const selectedCount = computed(() => exportDocuments.value.filter((doc) => doc.isSelected).length)

  // Calculate total size of selected documents
  const totalSelectedSize = computed(() => {
    return exportDocuments.value
      .filter((doc) => doc.isSelected)
      .reduce((total, doc) => total + (doc.size || 0), 0)
  })

  // Format the total size
  const totalSelectedSizeFormatted = computed(() => {
    return formatFileSize(totalSelectedSize.value)
  })

  // Check if total size exceeds RaMicroAPI limit
  const exceedsRaMicroApiLimit = computed(() => {
    return isRaMicroApiMode.value && totalSelectedSize.value > maxRaMicroApiFileSize.value
  })

  // Watch for changes in total size and emit validation status
  watch(exceedsRaMicroApiLimit, (exceeds) => {
    emit('sizeValidation', !exceeds)
  })

  const toggleSelection = (doc: ExportDocument, isSelected: boolean) => {
    // Prevent selection if adding this document would exceed RaMicroAPI limit
    if (isRaMicroApiMode.value && isSelected) {
      const newTotalSize = totalSelectedSize.value + (doc.size || 0)
      if (newTotalSize > maxRaMicroApiFileSize.value) {
        return
      }
    }

    doc.isSelected = isSelected
    emitFileData()
  }

  const deselectAll = () => {
    exportDocuments.value.forEach((doc) => {
      doc.isSelected = false
    })
    updateTrigger.value += 1
    nextTick(() => {
      emitFileData()
    })
  }

  const viewDocument = (doc: ExportDocument) => {
    window.open(doc.url, '_blank')
  }

  const editDocument = (doc: ExportDocument) => {
    doc.isEditing = true
    doc.newName = doc.filename.replace('.pdf', '')
  }

  const saveDocumentName = (doc: ExportDocument) => {
    doc.filename = `${doc.newName}.pdf`
    doc.isEditing = false
    emitFileData()
  }

  const cancelEdit = (doc: ExportDocument) => {
    doc.isEditing = false
    doc.newName = doc.filename.replace('.pdf', '')
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B'

    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const fileData = computed(() => {
    return exportDocuments.value
      .filter((doc) => doc.isSelected)
      .map((doc) => ({
        uuid: doc.uuid,
        filename: doc.filename
      }))
  })

  const emitFileData = () => {
    emit('update:fileData', fileData.value)
    emit(
      'updateDocuments',
      exportDocuments.value.filter((doc) => doc.isSelected)
    )
  }
</script>

<template>
  <div class="rounded-xl border bg-white p-6 shadow-md">
    <h2 class="mb-4 text-2xl font-bold text-gray-900">Dokumente ({{ selectedCount }})</h2>
    <p class="mb-4 text-gray-600">
      Wählen Sie aus, welche Dokumente Sie zur Aktenanlage verwenden möchten.
      <span
        v-if="isRaMicroApiMode"
        class="mt-2 block font-medium"
        :class="{
          'text-amber-600': exceedsRaMicroApiLimit,
          'text-gray-600': !exceedsRaMicroApiLimit
        }"
      >
        Gesamtgröße der ausgewählten Dokumente: {{ totalSelectedSizeFormatted }} /
        {{ maxRaMicroApiFileSizeMb }}MB
        <span
          v-if="exceedsRaMicroApiLimit"
          class="font-bold text-red-600"
        >
          (Limit überschritten! Bitte entfernen Sie einige Dokumente
          <template v-if="maxRaMicroApiFileSizeMb < 100">
            oder erhöhen Sie das Limit
            <a
              href="/app/settings/law-office-software/ra-micro"
              class="underline hover:text-red-800"
            >
              hier
            </a>
          </template>
          .)
        </span>
      </span>
    </p>
    <Table>
      <TableHeader>
        <TableRow class="bg-slate-50">
          <TableHead>
            <Button
              v-if="selectedCount > 0"
              variant="default"
              size="icon"
              class="h-5 w-5 p-0"
              @click="deselectAll"
            >
              <Minus class="h-3 w-3" />
            </Button>
          </TableHead>
          <TableHead>Dokument Name</TableHead>
          <TableHead>Ursprung</TableHead>
          <TableHead>Erstellungsdatum</TableHead>
          <TableHead>Größe</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow
          v-for="doc in exportDocuments"
          :key="`${doc.uuid}-${updateTrigger}`"
          class="hover:bg-slate-50"
        >
          <TableCell class="w-12">
            <CheckboxField
              v-model="doc.isSelected"
              :name="`document-${doc.uuid}`"
              :checkedValue="true"
              :uncheckedValue="false"
              @update:model-value="(isSelected: boolean) => toggleSelection(doc, isSelected)"
            />
          </TableCell>
          <TableCell class="p-2">
            <div class="flex items-center justify-between">
              <div class="flex-grow">
                <template v-if="doc.isEditing">
                  <div class="flex items-center">
                    <Input
                      v-model="doc.newName"
                      class="w-full"
                    />
                    <span class="ml-2 text-gray-500">.pdf</span>
                  </div>
                </template>
                <template v-else>
                  <span>{{ doc.filename.replace('.pdf', '') }}</span>
                  <span class="text-gray-500">.pdf</span>
                </template>
              </div>
              <div class="ml-2 flex items-center space-x-2">
                <template v-if="doc.isEditing">
                  <Button
                    variant="secondary"
                    size="icon"
                    class="h-8 w-8 p-0"
                    @click="saveDocumentName(doc)"
                  >
                    <Check class="h-4 w-4 text-green-500" />
                  </Button>
                  <Button
                    variant="secondary"
                    size="icon"
                    class="h-8 w-8 p-0"
                    @click="cancelEdit(doc)"
                  >
                    <X class="h-4 w-4 text-red-500" />
                  </Button>
                </template>
                <template v-else>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="h-8 w-8 p-0"
                    @click="viewDocument(doc)"
                  >
                    <Eye class="h-4 w-4 text-slate-500" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    class="h-8 w-8 p-0"
                    @click="editDocument(doc)"
                  >
                    <Pencil class="h-4 w-4 text-slate-500" />
                  </Button>
                </template>
              </div>
            </div>
          </TableCell>
          <TableCell class="p-2">
            {{ doc.document_type || 'Unknown' }}
          </TableCell>
          <TableCell class="p-2">
            {{ formatDate(doc.created_at) }}
          </TableCell>
          <TableCell class="p-2">
            {{ doc.sizeFormatted }}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </div>
</template>
